import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getDashboardStats } from '../services/dashboardApi';
import { DashboardStatsResponse, GarageResponse } from '../types';
import { useAuth } from '../hooks/useAuth';
import { useFeatureFlags } from '../context/FeatureFlagsContext';
import DashboardV2Page from './DashboardV2Page';

const STAT_CARDS = [
  { key: 'totalGarages' as const, label: 'Total Garages', icon: '🏢', color: 'primary' },
  { key: 'pendingGarages' as const, label: 'Pending', icon: '⏳', color: 'warning' },
  { key: 'approvedGarages' as const, label: 'Approved', icon: '✅', color: 'success' },
  { key: 'rejectedGarages' as const, label: 'Rejected', icon: '❌', color: 'danger' },
  { key: 'updateRequestedGarages' as const, label: 'Update Requested', icon: '📝', color: 'info' },
];

function StatusBadge({ status }: { status: string }) {
  const cls = status === 'PENDING' ? 'pending'
    : status === 'APPROVED' ? 'approved'
    : status === 'REJECTED' ? 'rejected'
    : status === 'UPDATE_REQUESTED' ? 'update-requested'
    : 'pending';
  return <span className={`admin-badge ${cls}`}>{status.replace('_', ' ')}</span>;
}

/**
 * Spec 069 AC-A1 — `garagat.admin-dashboard.v2-enabled` picks the work-queue dashboard; off (or the
 * switch unreadable) keeps the garage-status dashboard below, unchanged.
 */
export default function DashboardPage() {
  const features = useFeatureFlags();
  if (!features.loaded) {
    return <div className="admin-loading"><div className="admin-loading-spinner"></div></div>;
  }
  return features.dashboardV2 ? <DashboardV2Page /> : <LegacyDashboard />;
}

function LegacyDashboard() {
  const auth = useAuth();
  const [stats, setStats] = useState<DashboardStatsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!auth.token) return;
    getDashboardStats(auth.token)
      .then(setStats)
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [auth.token]);

  if (loading) {
    return (
      <div className="admin-loading">
        <div className="admin-loading-spinner"></div>
        <p style={{ marginTop: '0.75rem', color: 'var(--admin-text-muted)' }}>Loading dashboard...</p>
      </div>
    );
  }

  if (error) {
    return <div className="error-message">{error}</div>;
  }

  if (!stats) return null;

  return (
    <div>
      <h2 style={{ fontSize: '1.25rem', fontWeight: 600, color: 'var(--admin-text)', marginBottom: '1.25rem' }}>Dashboard</h2>

      <div className="admin-stats-row">
        {STAT_CARDS.map((card) => (
          <div className="admin-stat-card" key={card.key}>
            <div className={`admin-stat-icon ${card.color}`}>{card.icon}</div>
            <div>
              <div className="admin-stat-value">{stats[card.key]}</div>
              <div className="admin-stat-label">{card.label}</div>
            </div>
          </div>
        ))}
      </div>

      <div className="admin-card">
        <div className="admin-card-header">
          <h3 className="admin-card-title">Recent Pending Registrations</h3>
          <Link to="/garages?status=PENDING" className="admin-btn admin-btn-outline admin-btn-sm">
            View All
          </Link>
        </div>

        {stats.recentRegistrations.length === 0 ? (
          <div className="admin-empty-state">
            <p>No pending registrations.</p>
          </div>
        ) : (
          <div className="admin-table-wrapper">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Garage Name</th>
                  <th>Email</th>
                  <th>Status</th>
                  <th>Registered</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {stats.recentRegistrations.map((g: GarageResponse) => (
                  <tr key={g.id}>
                    <td style={{ fontWeight: 500 }}>{g.name}</td>
                    <td>{g.email || '-'}</td>
                    <td><StatusBadge status={g.status} /></td>
                    <td>{new Date(g.createdAt).toLocaleDateString()}</td>
                    <td>
                      <Link to={`/garages/${g.id}`} className="admin-link">
                        View
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
